import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useDesignedKeyboard } from '@/hooks/keyboard/use-designed-keyboard';
import { useIsDesignCompleted } from '@/hooks/keyboard/use-design-completion';
import { useMeasureOptions } from '@/hooks/store/use-measure-options-store';
import { useTranslation } from '@/hooks/use-translation';
export interface NavigationLink {
  isDisabled?: boolean;
  label: string;
  /** Short mono tag shown after the label, e.g. OPTION. */
  tag?: string;
  to: string;
}
export interface NavigationStep {
  label: string;
  links: NavigationLink[];
  /**
   * Two-digit step number, shown in brass in the sidebar and breadcrumb.
   * The piano has none: it is the instrument the workflow is about, not a
   * step of it, and numbering it would start the list at zero.
   */
  number?: string;
  /** URL prefix owning every page of the step. */
  section: string;
  /** Where the step itself leads: its own page, or its first open link. */
  to: string;
}
const firstOpenLink = (links: NavigationLink[]): string | undefined => links.find(link => !link.isDisabled)?.to;

/**
 * The four steps of the workflow, in order.
 *
 * One model, three readers: the sidebar renders it, and the breadcrumb of the
 * page header and the title of the document look the current route up in
 * it. A new page is also declared to the router (`AppRouter.tsx`), and a
 * Design page to the steps bar (`use-design-steps.ts`), which adds what each
 * step is set to.
 */
export const useAppNavigation = () => {
  const $ = _c(40);
  const {
    t
  } = useTranslation();
  const {
    useSupportSpringMeasurements
  } = useMeasureOptions();
  const designedKeyboard = useDesignedKeyboard();
  const isDesignCompleted = useIsDesignCompleted(designedKeyboard);
  let t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== isDesignCompleted || $[1] !== t || $[2] !== useSupportSpringMeasurements) {
    const analyzeLinks = [{
      label: t("Touch Weight", {
        ns: "navigation"
      }),
      to: "/analyze/touch-weight"
    }, {
      label: t("Front Weight", {
        ns: "navigation"
      }),
      to: "/analyze/front-weight"
    }, {
      label: t("Strike Weight", {
        ns: "navigation"
      }),
      to: "/analyze/strike-weight"
    }, {
      label: t("Strike Weight Ratio", {
        ns: "navigation"
      }),
      to: "/analyze/strike-weight-ratio"
    }, {
      label: t("Wippen Support Springs", {
        ns: "navigation"
      }),
      tag: useSupportSpringMeasurements ? undefined : t("Option", {
        ns: "navigation"
      }),
      to: "/analyze/wippen-support-springs"
    }, {
      label: t("Data Sheet", {
        ns: "navigation"
      }),
      to: "/analyze/data-sheet"
    }];
    const designLinks = [{
      label: t("Wippen Support Springs", {
        ns: "navigation"
      }),
      to: "/design/wippen-support-springs"
    }, {
      label: t("Front Weight", {
        ns: "navigation"
      }),
      to: "/design/front-weight"
    }, {
      label: t("Strike Weight", {
        ns: "navigation"
      }),
      to: "/design/strike-weight"
    }, {
      label: t("Strike Weight Ratio", {
        ns: "navigation"
      }),
      to: "/design/strike-weight-ratio"
    }, {
      isDisabled: !isDesignCompleted,
      label: t("Touch Weight Preview", {
        ns: "navigation"
      }),
      to: "/design/touch-weight-preview"
    }, {
      isDisabled: !isDesignCompleted,
      label: t("Adjustment Sheet", {
        ns: "navigation"
      }),
      to: "/design/adjustment-sheet"
    }];
    let t8;
    if ($[11] !== t) {
      t8 = t("Piano", {
        ns: "navigation"
      });
      $[11] = t;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    let t9;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = [];
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    if ($[14] !== t8) {
      t5 = {
        label: t8,
        links: t9,
        section: "/piano",
        to: "/piano"
      };
      $[14] = t8;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    let t10;
    if ($[16] !== t) {
      t10 = t("Measure", {
        ns: "navigation"
      });
      $[16] = t;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    let t11;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = [];
      $[18] = t11;
    } else {
      t11 = $[18];
    }
    if ($[19] !== t10) {
      t6 = {
        label: t10,
        links: t11,
        number: "01",
        section: "/measure",
        to: "/measure"
      };
      $[19] = t10;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    let t12;
    if ($[21] !== t) {
      t12 = t("Analyze", {
        ns: "navigation"
      });
      $[21] = t;
      $[22] = t12;
    } else {
      t12 = $[22];
    }
    const t13 = firstOpenLink(analyzeLinks) ?? "/analyze/touch-weight";
    if ($[23] !== analyzeLinks || $[24] !== t12 || $[25] !== t13) {
      t7 = {
        label: t12,
        links: analyzeLinks,
        number: "02",
        section: "/analyze",
        to: t13
      };
      $[23] = analyzeLinks;
      $[24] = t12;
      $[25] = t13;
      $[26] = t7;
    } else {
      t7 = $[26];
    }
    if ($[27] !== t) {
      t0 = t("Design", {
        ns: "navigation"
      });
      $[27] = t;
      $[28] = t0;
    } else {
      t0 = $[28];
    }
    t1 = designLinks;
    t2 = "03";
    t3 = "/design";
    t4 = firstOpenLink(designLinks) ?? "/design/wippen-support-springs";
    $[0] = isDesignCompleted;
    $[1] = t;
    $[2] = useSupportSpringMeasurements;
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t0 = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
    t7 = $[10];
  }
  let t8;
  if ($[29] !== t0 || $[30] !== t1 || $[31] !== t2 || $[32] !== t3 || $[33] !== t4) {
    t8 = {
      label: t0,
      links: t1,
      number: t2,
      section: t3,
      to: t4
    };
    $[29] = t0;
    $[30] = t1;
    $[31] = t2;
    $[32] = t3;
    $[33] = t4;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== t5 || $[36] !== t6 || $[37] !== t7 || $[38] !== t8) {
    t9 = [t5, t6, t7, t8];
    $[35] = t5;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  return t9;
};
export interface NavigationLocation {
  link?: NavigationLink;
  step?: NavigationStep;
}

/** Finds the step, and where possible the page, a pathname belongs to. */
export const locateInNavigation = (steps: NavigationStep[], pathname: string): NavigationLocation => {
  const step = steps.find(candidate => pathname === candidate.section || pathname.startsWith(`${candidate.section}/`));
  if (!step) {
    return {};
  }
  return {
    link: step.links.find(link => link.to === pathname),
    step
  };
};